import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { theme } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { PortalLink } from "./PortalLink";
import { ASSET_KINDS, ASSET_SOURCE_LABELS, SeverityPill, labelOf } from "../pages/resilience/shared";

// The "Affects N assets" chip on a threat advisory and the list behind it: which of the
// organisation's assets the advisory reaches, in three tiers the server decides
// (backend services/advisoryExposure.ts + lib/assetAdvisoryMatch.ts):
//   confirmed — Defender reports the CVE on the device, or the installed OS / firmware /
//               software version is inside a range the NVD publishes;
//   likely    — the product matches but the version could not be compared;
//   possible  — the advisory's words name a product, supplier or asset on the register.
// The counts ride along on GET /threat-feeds; the assets come from
// GET /threat-feeds/:id/assets when the chip is opened.

/** GET /threat-feeds affectedAssets[itemId]. */
export interface AffectedAssetCount { count: number; important: number; confirmed?: number; likely?: number; possible?: number }

export type ExposureConfidence = "confirmed" | "likely" | "possible";

interface AffectedAsset {
  id: string;
  name: string;
  kind: string;
  criticality: string;
  isEssentialFunction: boolean;
  source: string;
  reason: string;
  confidence: ExposureConfidence;
  basis: string;
  cveId: string | null;
  firstSeenAt: string | null;
}

export const CONFIDENCE_META: Record<ExposureConfidence, { label: string; color: string; bg: string; title: string }> = {
  confirmed: { label: "Confirmed", color: theme.color.dangerText,  bg: theme.color.dangerBg,  title: "Defender reports the CVE on the device, or the installed version is inside the affected range the NVD publishes" },
  likely:    { label: "Likely",    color: theme.color.warningText, bg: theme.color.warningBg, title: "The product matches but the version could not be compared (no range published, or the version is not recorded)" },
  possible:  { label: "Possible",  color: theme.color.accentText,  bg: theme.color.accentBg,  title: "The advisory's words name a product, supplier or asset on the register" },
};

export function ConfidencePill({ confidence }: { confidence: ExposureConfidence }) {
  const m = CONFIDENCE_META[confidence];
  return <span title={m.title} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: m.bg, color: m.color, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{m.label}</span>;
}

const BASIS_LABELS: Record<string, string> = { defender: "Defender Vulnerability Management", version: "version inside the affected range", product: "product match", text: "named in the advisory" };

export function AdvisoryAffectedAssets({ itemId, affected }: { itemId: string; affected: AffectedAssetCount | undefined }) {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState<AffectedAsset[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!affected || affected.count === 0) return null;
  const confirmed = affected.confirmed ?? 0;
  const likely = affected.likely ?? 0;
  const possible = affected.possible ?? affected.count - confirmed - likely;
  const important = affected.important > 0;
  // Confirmed exposure is the danger colour; likely (or an important asset) the warning colour; possible the accent.
  const tone = confirmed > 0 ? "danger" : likely > 0 || important ? "warning" : "accent";
  const colours = tone === "danger" ? { border: theme.color.dangerText, bg: theme.color.dangerBg, text: theme.color.dangerText }
    : tone === "warning" ? { border: theme.color.warningText, bg: theme.color.warningBg, text: theme.color.warningText }
    : { border: theme.color.accent, bg: theme.color.accentBg, text: theme.color.accentText };
  const parts = [confirmed > 0 ? `${confirmed} confirmed` : null, likely > 0 ? `${likely} likely` : null, possible > 0 ? `${possible} possible` : null].filter(Boolean);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next || assets !== null || loading) return;
    setLoading(true); setError(null);
    try { setAssets((await api.get<{ assets: AffectedAsset[] }>(`/threat-feeds/${itemId}/assets`)).assets); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the affected assets")); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div>
        <button
          type="button" onClick={toggle} aria-expanded={open}
          title={confirmed > 0 ? "An asset is confirmed vulnerable — patch or mitigate, then record the triage" : important ? "Names a critical, high or essential-function asset — triage first" : "Names assets on your register"}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 3, cursor: "pointer", border: "1px solid",
            borderColor: colours.border, background: colours.bg, color: colours.text,
          }}
        >
          Affects {affected.count} asset{affected.count === 1 ? "" : "s"}{parts.length ? ` · ${parts.join(" · ")}` : ""}{important ? ` · ${affected.important} important` : ""}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      {open && (
        <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, background: theme.color.panelAlt, padding: "8px 12px", display: "grid", gap: 8 }}>
          {loading ? (
            <span style={{ fontSize: 12, color: theme.color.muted }}>Matching against the asset register…</span>
          ) : error ? (
            <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>
          ) : assets && assets.length === 0 ? (
            <span style={{ fontSize: 12, color: theme.color.muted }}>No active asset matches this advisory any more.</span>
          ) : (
            (assets ?? []).map((a) => (
              <div key={a.id} style={{ display: "grid", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <ConfidencePill confidence={a.confidence} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>{a.name}</span>
                  <SeverityPill value={a.criticality} />
                  {a.isEssentialFunction && <span style={{ fontSize: 10, fontWeight: 600, color: theme.color.warningText }}>Essential function</span>}
                  <span style={{ fontSize: 11, color: theme.color.subtle }}>{labelOf(ASSET_KINDS, a.kind)}{a.source !== "manual" ? ` · ${ASSET_SOURCE_LABELS[a.source] ?? a.source}` : ""}</span>
                  {a.cveId && <span style={{ fontSize: 10, fontVariantNumeric: "tabular-nums", color: theme.color.subtle, marginLeft: "auto" }}>{a.cveId}</span>}
                </div>
                <span style={{ fontSize: 11, color: theme.color.muted, lineHeight: 1.4 }}>
                  {a.reason}
                  {a.confidence !== "possible" && <span style={{ color: theme.color.faint }}> · {BASIS_LABELS[a.basis] ?? a.basis}{a.firstSeenAt ? `, since ${new Date(a.firstSeenAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}</span>}
                </span>
              </div>
            ))
          )}
          <PortalLink to="/resilience/assets" style={{ fontSize: 11, color: theme.color.accentText, textDecoration: "none", fontWeight: 600 }}>Open the asset register</PortalLink>
        </div>
      )}
    </div>
  );
}
